import { YZCanvasContext } from "../../canvas";
import { type TSVGParsedPathData } from "../../svg/DOMSVGUtil";
import { angle } from "../math";

export type TLOptions = {
  x?: number;
  y?: number;
  toX?: number;
  toY?: number;
};

/// 配合 PathD 使用，不可单独使用
export class L {
  x: number;
  y: number;
  toX: number;
  toY: number;

  constructor(options?: TLOptions) {
    this.x = options?.x || 0;
    this.y = options?.y || 0;
    this.toX = options?.toX || 0;
    this.toY = options?.toY || 0;
  }

  toSVGPathData(): TSVGParsedPathData[] {
    return [["L", this.toX, this.toY]];
  }

  toCanvasContext(ctx: YZCanvasContext) {
    ctx.lineTo(this.toX, this.toY);
  }

  getBoundingClientRect() {
    const lineAngle = angle(this.x, this.y, this.toX, this.toY);
    if (lineAngle === 0) {
      const minX = Math.min(this.x, this.toX);
      const minY = Math.min(this.y, this.toY);
      const maxX = Math.max(this.x, this.toX);
      const maxY = Math.max(this.y, this.toY);
      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      };
    } else {
      const sin = Math.sin(lineAngle);
      const cos = Math.cos(lineAngle);
      const x1r = cos * this.x - sin * this.y;
      const x2r = cos * this.toX - sin * this.toY;
      const y1r = cos * this.y + sin * this.x;
      const y2r = cos * this.toY + sin * this.toX;
      return {
        x: Math.min(x1r, x2r),
        y: Math.min(y1r, y2r),
        width: Math.max(x1r, x2r) - Math.min(x1r, x2r),
        height: Math.max(y1r, y2r) - Math.min(y1r, y2r)
      };
    }
  }

  // t : The fraction of the overall duration of the path. (A value from 0.0 to 1.0)
  interpolate(t: number): { x: number; y: number } {
    return {
      x: this.x + t * (this.toX - this.x),
      y: this.y + t * (this.toY - this.y)
    };
  }

  intersection(line: L): { x: number; y: number } | null {
    const x1 = this.x;
    const y1 = this.y;
    const x4 = line.toX;
    const y4 = line.toY;
    const dx1 = this.toX - x1;
    const dx2 = line.x - x4;
    if (!dx1 && !dx2) {
      return null;
    }
    const m1 = (this.toY - y1) / dx1;
    const m2 = (line.y - y4) / dx2;
    if (!dx1) {
      // infinity
      return { x: x1, y: m2 * (x1 - x4) + y4 };
    } else if (!dx2) {
      // infinity
      return { x: x4, y: m1 * (x4 - x1) + y1 };
    }
    const xInt = (-m2 * x4 + y4 + m1 * x1 - y1) / (m1 - m2);
    const yInt = m1 * (xInt - x1) + y1;
    if (isNaN(xInt) || isNaN(yInt)) {
      return null;
    }
    return { x: xInt, y: yInt };
  }
}

export default L;
